import { useState } from "react";
import {
  Alert, Box, Button, Chip, Collapse, Link, Stack, Step, StepLabel, Stepper, Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { PAYMENT_REQUEST_STATUS, PAYMENT_REQUEST_STATUS_UI } from "../../../../constants";
import { PAYMENT_AREAS, getAreaOf, isPostApproval } from "./paymentRequestLogic";
import { formatReviewMoment } from "./paymentReviewUi";

// Recorrido financiero de una solicitud de pago jurídica (acta #3): Causación →
// Contabilidad → Presupuesto → Tesorería → Caja. Responde las tres preguntas
// del acta —en qué área está, cuál fue el último movimiento y si fue devuelta—
// leyendo `status` y `financial.history/returns` (D5). Es de SOLO LECTURA para
// todo el mundo: la empresa, el supervisor, el gestor y las áreas financieras
// ven exactamente lo mismo; las acciones viven en la vista de Gestión de Pagos.
//
// Colores y etiquetas de estado salen de PAYMENT_REQUEST_STATUS_UI; aquí no se
// decide ningún color de estado.

const statusLabel = (status) => PAYMENT_REQUEST_STATUS_UI[status]?.label || status || "";

// Texto de un movimiento del historial, con el vocabulario del acta.
export const describeMovement = (entry) => {
  if (!entry) return "";
  if (entry.action === "RECEIVE") return "Recibida en Causación";
  if (entry.action === "RETURN") return `Devuelta a Causación desde ${entry.fromLabel || statusLabel(entry.from)}`;
  if (entry.to === PAYMENT_REQUEST_STATUS.PAYMENT_NOTIFIED) return "Pago notificado por Tesorería";
  if (entry.to === PAYMENT_REQUEST_STATUS.PAID) return "Pago registrado en Caja";
  return `Enviada a ${statusLabel(entry.to).replace(/^En /, "")}`;
};

const formatCalendar = (iso) => {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const PaymentFlowTrail = ({ request }) => {
  const [showHistory, setShowHistory] = useState(false);

  if (!request || !isPostApproval(request.status)) return null;

  const financial = request.financial || {};
  const history = Array.isArray(financial.history) ? financial.history : [];
  const returns = Array.isArray(financial.returns) ? financial.returns : [];
  const isPaid = request.status === PAYMENT_REQUEST_STATUS.PAID;
  const area = getAreaOf(request.status);
  const areaIndex = Math.max(0, PAYMENT_AREAS.findIndex((a) => a.key === area?.key));
  const last = history[history.length - 1] || null;
  const lastReturn = returns[returns.length - 1] || null;
  // "Devuelta" se señala mientras el último movimiento sea la devolución: en
  // cuanto Causación la vuelve a impulsar, pasa a ser parte del historial.
  const returnedNow = last?.action === "RETURN";
  const returnedFromKey = lastReturn ? getAreaOf(lastReturn.fromStatus)?.key : null;
  const statusUi = PAYMENT_REQUEST_STATUS_UI[request.status] || {};
  const payment = financial.payment || null;
  const cashed = financial.cashed || null;

  return (
    <Box>
      <Stepper activeStep={isPaid ? PAYMENT_AREAS.length : areaIndex} alternativeLabel sx={{ mb: 2 }}>
        {PAYMENT_AREAS.map((a, i) => {
          const current = !isPaid && i === areaIndex;
          const returnedFromHere = returnedNow && a.key === returnedFromKey;
          return (
            <Step key={a.key} completed={isPaid || i < areaIndex}>
              <StepLabel
                error={returnedFromHere}
                optional={
                  current ? (
                    <Typography variant="caption" color="text.secondary">Actual</Typography>
                  ) : returnedFromHere ? (
                    <Typography variant="caption" color="error">Devolvió</Typography>
                  ) : null
                }
              >
                {a.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={statusUi.label || request.status}
          color={statusUi.color}
          sx={statusUi.sx}
          icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
        />
        {!returnedNow && returns.length > 0 && (
          <Chip
            size="small"
            variant="outlined"
            icon={<AssignmentReturnIcon />}
            label={`${returns.length} devolución${returns.length === 1 ? "" : "es"} en el recorrido`}
          />
        )}
      </Stack>

      <Typography variant="body2" sx={{ mt: 1.5 }}>
        <strong>Último movimiento:</strong>{" "}
        {last
          ? `${describeMovement(last)} · ${last.byName || "—"} · ${formatReviewMoment(last.at)}`
          : `Aprobada por supervisión${request.approvedByName ? ` (${request.approvedByName})` : ""}${
            request.approvedAt ? ` el ${formatReviewMoment(request.approvedAt)}` : ""
          }, pendiente de recepción en Causación`}
      </Typography>

      {returnedNow && lastReturn && (
        <Alert severity="warning" icon={<AssignmentReturnIcon />} sx={{ mt: 1.5 }}>
          Devuelta desde <strong>{lastReturn.fromLabel}</strong>
          {lastReturn.byName ? ` por ${lastReturn.byName}` : ""} el {formatReviewMoment(lastReturn.at)}.
          {lastReturn.message ? (
            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
              “{lastReturn.message}”
            </Typography>
          ) : null}
        </Alert>
      )}

      {payment && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <strong>Pago:</strong> notificado por {payment.notifiedByName || "Tesorería"} el{" "}
          {formatReviewMoment(payment.notifiedAt)}
          {payment.paidAt ? ` · pagado en banco el ${formatCalendar(payment.paidAt) || payment.paidAt}` : ""}
          {payment.reference ? ` · referencia ${payment.reference}` : ""}
        </Typography>
      )}

      {cashed && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Caja:</strong> registrado por {cashed.byName || "Caja"} el {formatReviewMoment(cashed.at)}
          {cashed.proof?.url ? (
            <>
              {" · "}
              <Link href={cashed.proof.url} target="_blank" rel="noopener" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                {cashed.proof.name || "Comprobante"} <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            </>
          ) : null}
        </Typography>
      )}

      {history.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? "Ocultar historial" : `Ver historial (${history.length})`}
          </Button>
          <Collapse in={showHistory}>
            <Stack spacing={0.75} sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: "divider" }}>
              {history.map((entry, i) => (
                <Box key={`${entry.at}-${i}`}>
                  <Typography variant="body2">
                    {formatReviewMoment(entry.at)} · {describeMovement(entry)}
                    {entry.byName ? ` · ${entry.byName}` : ""}
                  </Typography>
                  {entry.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      “{entry.message}”
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default PaymentFlowTrail;
